import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Auto-detect FormData and let the browser set the multipart boundary header.
// Without this, axios would keep the default `application/json` and the body
// would arrive at the FastAPI Form(...) backend as JSON → 422 missing fields.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    // Delete so the browser injects multipart/form-data; boundary=...
    delete config.headers['Content-Type'];
    if (config.headers.common) delete config.headers.common['Content-Type'];
  }
  return config;
});

// Convert FastAPI 422 validation arrays into a human-readable string so callers
// can pass `error.response?.data?.detail` straight to toast() without crashing
// React with "Objects are not valid as a React child".
export const extractErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : d?.loc;
        return field ? `${field}: ${d?.msg || 'invalid'}` : (d?.msg || JSON.stringify(d));
      })
      .join(' · ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return fallback;
};

// Handle auth errors + flatten FastAPI validation arrays so callers can safely
// render `error.response.data.detail` (which used to crash React for 422s).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Flatten 422 validation arrays in place
    const d = error?.response?.data?.detail;
    if (d && (Array.isArray(d) || typeof d === 'object')) {
      error.response.data.detail = extractErrorMessage(error);
    }
    return Promise.reject(error);
  }
);

export default api;

// Trigger a browser download for a PDF report endpoint. Uses the same axios
// instance so the auth interceptor stays the single source of truth for the
// Bearer token. Falls back to `filename` when the server does not send a
// Content-Disposition header.
export const downloadPdf = async (path, filename = 'report.pdf') => {
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
